/*
Radar obscure store — easter egg v1.3.1 (mirror Android
`RadarObscureStore.kt`). Persiste un seul booléen `radarObscured` dans
le store IDB `meta`. Mécanique :

- Activé en triple-tapant le radar du `HuntPanel` (3 taps en < 600 ms
  cumulés). Caché, non communiqué dans l'UI.
- Quand actif, masque le titre + la qualification de la cible courante
  par « ??? ». La distance live reste affichée. Le FAB ★ d'entrée n'est
  pas affecté.
- Bidirectionnel : un nouveau triple-tap revient au mode normal
  (parité Android `toggle()`).

Pattern : équivalent direct d'`onboardingStore.ts` côté PWA. IDB `meta`
est sélectionné parce qu'il sert déjà aux flags de session
(`onboardingDone`, `splashIntroSeen`) et qu'il survit aux refresh +
navigations cold-start, ce qui matche DataStore Preferences Android.
*/

import { kStores, withTx } from './db'

const kKeyRadarObscured: string = 'radarObscured'

interface MetaRow
{
	key: string
	value: unknown
}

async function readBool(inKey: string): Promise<boolean>
{
	return withTx([kStores.meta], 'readonly', (inTx) => {
		return new Promise<boolean>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.meta).get(inKey)
			vReq.onsuccess = () => {
				const vRow = vReq.result as MetaRow | undefined
				inResolve(vRow?.value === true)
			}
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

async function writeBool(inKey: string, inValue: boolean): Promise<void>
{
	return withTx([kStores.meta], 'readwrite', (inTx) => {
		return new Promise<void>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.meta).put({ key: inKey, value: inValue })
			vReq.onsuccess = () => inResolve()
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Read the current obscured state. Returns false on cold IDB (missing
row), matching Android DataStore `prefs[OBSCURED_KEY] ?: false`.
*/
export async function getRadarObscured(): Promise<boolean>
{
	return readBool(kKeyRadarObscured)
}

/*
Persist the next obscured state (write-only convenience used by
HuntPanel triple-tap). Best-effort : a failing IDB write does not
crash the panel — the in-memory toggle stays in effect for the
current session and the next read returns the prior value.
*/
export async function setRadarObscured(inValue: boolean): Promise<void>
{
	await writeBool(kKeyRadarObscured, inValue)
}
